import {
  GuestFlightScheduleLiteModel,
  GuestHotelScheduleLiteModel,
} from 'api-hooks/cro/model';
import {
  useUpdateFlightSchedule,
  useUpdateHotelSchedule,
} from 'api-hooks/cro/mutation';
import {
  HotelCheckInStatusEnum,
  FlightCheckInStatusEnum,
} from 'common/constants/enum';
import { ModelTransformer } from 'common/helpers/common';
import { ExtendedApiResult } from 'common/repositories/common.model';
import { queryClient } from 'common/repositories/query-client';
import notification from 'components/common/notification';
import Select from 'components/elements/master/select-input';
import {
  flightCheckInOptions,
  hotelCheckInOptions,
} from 'components/elements/table/editable-columns/check-in-options';
import useTranslation from 'next-translate/useTranslation';
import React from 'react';

interface CheckInSelectProps {
  original: GuestFlightScheduleLiteModel | GuestHotelScheduleLiteModel;
  type: 'flight' | 'hotel';
}

interface CheckInOptions {
  id: FlightCheckInStatusEnum | HotelCheckInStatusEnum;
  label: string;
}

// optimistic
export default function CheckInSelect(props: CheckInSelectProps) {
  const { type } = props;
  const { t } = useTranslation();
  const storageKey =
    type === 'flight' ? 'flight-schedule-key' : 'hotel-schedule-key';

  const fallbackStorageValue =
    type === 'flight'
      ? "['get-guest-flight-schedules']"
      : "['get-guest-hotel-schedules']";

  const key = sessionStorage.getItem(storageKey);

  const transformer =
    type === 'flight'
      ? GuestFlightScheduleLiteModel
      : GuestHotelScheduleLiteModel;

  const mutate =
    type === 'flight' ? useUpdateFlightSchedule : useUpdateHotelSchedule;

  const queryKey = JSON.parse(key || fallbackStorageValue);

  // Snapshot the previous value
  const data = queryClient.getQueryData(queryKey) as ExtendedApiResult<any[]>;

  const original = ModelTransformer(
    data.data.find((value) => {
      return value.id === props.original.id;
    })!,
    transformer,
  );

  const id = props.original.id;

  const { mutateAsync } = mutate();

  const options: CheckInOptions[] = React.useMemo(() => {
    return type === 'flight' ? flightCheckInOptions(t) : hotelCheckInOptions(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [type]);

  const onGetValue = React.useCallback(
    (value: string) => {
      return options.filter((option) => {
        return option.id === value;
      });
    },
    [options],
  );

  return (
    <div>
      <div
        onClick={(e) => {
          e.preventDefault(); // stop bubbling on href
          e.stopPropagation(); // stop bubbling on component
        }}
      >
        <Select
          options={options}
          clearable={false}
          value={onGetValue(original.checkInStatus || '')}
          onChange={async (params) => {
            //optimistic update
            const value =
              type === 'flight'
                ? (params.option?.id as FlightCheckInStatusEnum)
                : (params.option?.id as HotelCheckInStatusEnum);

            try {
              await queryClient.cancelQueries({ queryKey });
              const result = data.data.map((item) => {
                if (item.id === id) {
                  return {
                    ...item,
                    check_in_status: value,
                  };
                }
                return item;
              });

              queryClient.setQueryData(queryKey, {
                ...data,
                data: result,
              });

              const inputMutateFlight = [
                {
                  id,
                  checkInStatus: value as FlightCheckInStatusEnum,
                },
              ];

              const inputMutateHotel = [
                {
                  id,
                  checkInStatus: value as HotelCheckInStatusEnum,
                  hotelConfirmationCode: original.hotelConfirmationCode,
                },
              ];

              const response = await mutateAsync({
                flightSchedules: inputMutateFlight,
                hotelSchedules: inputMutateHotel,
              });

              notification.success({
                message: response.message,
              });
            } catch (e) {
              queryClient.setQueryData(queryKey, queryKey);
              notification.error({
                message: e.message,
              });
            }
          }}
        />
      </div>
    </div>
  );
}
